import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScanSearch, AlertTriangle } from 'lucide-react';
import { axios, auth, mbUrl, muted, primary, runCall, toast } from './mbApi';

const ReadyRow = ({ r, on, toggle }) => (
  <label className="flex items-start gap-2 rounded-lg border border-[hsl(var(--border))] p-2 cursor-pointer" data-testid={`mb-am-ready-${r.test_id}`}>
    <Checkbox checked={on} onCheckedChange={toggle} className="mt-0.5" data-testid={`mb-am-check-${r.test_id}`} />
    <span className="text-sm min-w-0">
      <span className={`font-mono ${primary}`}>{r.test_code}</span> → Ad ID <span className="font-mono">{r.ad_id}</span>
      <span className={`block text-xs ${muted} truncate`}>{r.ad_name} · {r.evidence}</span>
    </span>
  </label>
);

const ManualRow = ({ r }) => (
  <div className="rounded-lg border border-amber-500/30 p-2 text-sm" data-testid={`mb-am-manual-${r.test_id}`}>
    <span className={`font-mono ${primary}`}>{r.test_code}</span> <span className="text-amber-400 text-xs">— {r.reason}</span>
    {r.candidates.map((c) => <p key={c.ad_id} className={`text-xs ${muted}`}>Calon: <span className="font-mono">{c.ad_id}</span> · {c.name}</p>)}
    <p className={`text-xs ${muted}`}>Tak dipadankan automatik. Masukkan Ad ID yang betul sendiri dalam ujian ni.</p>
  </div>
);

const PlanBody = ({ plan, picked, setPicked }) => {
  const ready = plan.rows.filter((r) => r.state === 'sedia');
  const manual = plan.rows.filter((r) => r.state === 'pilih_manual');
  const none = plan.rows.filter((r) => r.state === 'tiada_calon').length;
  const flip = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const all = ready.length > 0 && picked.length === ready.length;
  return (
    <div className="space-y-2 max-h-[55vh] overflow-y-auto">
      {ready.length > 1 && (
        <button type="button" className="text-xs underline" onClick={() => setPicked(all ? [] : ready.map((r) => r.test_id))} data-testid="mb-am-select-all">
          {all ? 'Nyahpilih semua' : `Pilih semua (${ready.length})`}
        </button>
      )}
      {plan.demo && <p className="text-xs text-amber-400">Channel demo — iklan contoh, bukan iklan Meta sebenar.</p>}
      {plan.partial && (
        <p className="text-xs text-amber-400 flex items-start gap-1" data-testid="mb-am-partial"><AlertTriangle size={14} className="shrink-0" />
          Bacaan Meta separa: {plan.errors.map((e) => e.message).join(' · ')}. Ujian yang tiada calon mungkin ada iklan yang belum dibaca.</p>
      )}
      {plan.message && <p className={`text-sm ${muted}`}>{plan.message}</p>}
      {ready.map((r) => <ReadyRow key={r.test_id} r={r} on={picked.includes(r.test_id)} toggle={() => flip(r.test_id)} />)}
      {manual.map((r) => <ManualRow key={r.test_id} r={r} />)}
      {none > 0 && <p className={`text-xs ${muted}`}>{none} ujian tiada calon iklan lagi (iklan belum dicipta atau URL tiada token).</p>}
    </div>
  );
};

export const AutoMatchButton = ({ ibId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [picked, setPicked] = useState([]);
  const scan = () => runCall(setBusy, async () => {
    const { data } = await axios.post(mbUrl(ibId, '/tests/auto-match'), {}, auth());
    setPicked([]);
    setPlan(data);
  });
  const confirm = () => runCall(setBusy, async () => {
    const { data } = await axios.post(mbUrl(ibId, '/tests/auto-match/confirm'), { plan_id: plan.id, test_ids: picked }, auth());
    toast.success(`${data.applied.length} Ad ID dipadankan${data.conflicts.length ? ` · ${data.conflicts.length} konflik tak ditulis` : ''}`);
    data.conflicts.forEach((c) => toast.warning(`${c.test_code}: ${c.reason}`));
    setPlan(null);
    onDone();
  });
  return (
    <>
      <Button size="sm" variant="outline" className="h-9 text-xs" disabled={busy} onClick={scan} title="Semak iklan Meta yang guna URL Iklan / kod ujian — tiada perubahan sehingga anda sahkan" data-testid="mb-shortcut-automatch">
        <ScanSearch size={13} className="mr-1.5" /> {busy && !plan ? 'Mencari…' : 'Cari Ad ID'}
      </Button>
      <Dialog open={!!plan} onOpenChange={(o) => !o && setPlan(null)}>
        <DialogContent className="max-w-lg" data-testid="mb-am-dialog">
          <DialogHeader>
            <DialogTitle>Semak padanan Ad ID</DialogTitle>
            <DialogDescription>Belum ada apa-apa diubah. Hanya padanan token tepat boleh disahkan; padanan nama perlu anda masukkan sendiri.</DialogDescription>
          </DialogHeader>
          {plan && <PlanBody plan={plan} picked={picked} setPicked={setPicked} />}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlan(null)} data-testid="mb-am-cancel">Batal</Button>
            <Button disabled={busy || !picked.length} onClick={confirm} data-testid="mb-am-confirm">Sahkan {picked.length} padanan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
